import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Wrench, Coffee, Scissors, Building2, Check } from 'lucide-react';

// Consistent animation variants (0.6s ease-out, subtle slide)
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } // smooth cubic-bezier
  }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

// Who We Build For data
const businessTypes = [
  {
    icon: Wrench,
    title: 'Trades',
    description: 'Plumbers, Electricians, Builders',
  },
  {
    icon: Coffee,
    title: 'Cafés & Restaurants',
    description: 'Food & hospitality businesses',
  },
  {
    icon: Scissors,
    title: 'Hair & Beauty',
    description: 'Salons, spas, and wellness',
  },
  {
    icon: Building2,
    title: 'Startups & Small Companies',
    description: 'Growing local businesses',
  },
];

// Portfolio images – stored in public/portfolio as card-01.jpg … card-05.jpg
// You can replace these files with your own images. Using the public folder
// means the paths can be referenced directly (no import needed).
const portfolioImages = [
  '/portfolio/card-01.jpg',
  '/portfolio/card-02.jpg',
  '/portfolio/card-03.jpg',
  '/portfolio/card-04.jpg',
  '/portfolio/card-05.jpg',
];

export default function HomePage() {
  return (
    <div className="pt-20">
      {/* ============================================
          HERO SECTION WITH BACKGROUND IMAGE & ANIMATIONS
      ============================================ */}
      <section className="hero-section relative flex items-center" data-testid="hero-section">
        {/* Background Container with Animated Image */}
        <div className="hero-background">
          {/* 
            Background Image with Performance Optimizations:
            - Fade-in animation (1.2s) + subtle zoom (8s) on page load
            - Lazy loading for performance
            - Responsive image sizes via srcSet
            - Dark overlay ensures text readability on any image
          */}
          <img
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=2000&q=80"
            alt="Abstract technology background"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            srcSet="
              https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=800&q=80 800w,
              https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1200&q=80 1200w,
              https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=2000&q=80 2000w
            "
            sizes="(max-width: 640px) 800px, (max-width: 1024px) 1200px, 2000px"
          />
          
          {/* Dark overlay for text readability */}
          <div className="hero-overlay" />
          
          {/* Cyan glow effect for visual depth */}
          <div className="hero-glow" />
        </div>

        <div className="section-container relative z-10 py-24 md:py-32 pb-48 md:pb-24">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Headline */}
            <motion.h1
              variants={fadeInUp}
              className="text-5xl sm:text-6xl lg:text-7xl font-black mb-8 leading-[1.1] tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="hero-headline"
            >
              Websites That Bring You{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-cyan-300">
                More Customers
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={fadeInUp}
              className="text-lg md:text-xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed"
              data-testid="hero-subheadline"
            >
              We build modern, professional websites for small businesses in Ireland 
              — starting from €990 or €59/month.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-24 lg:mb-0"
            >
              <Link
                to="/contact"
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="hero-cta-start"
              >
                Start Your Project
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Section divider */}
        <div className="absolute bottom-0 left-0 right-0 section-divider" />

        {/* Premium portfolio strip (auto-scrolling) */}
        <div className="hero-portfolio-strip" aria-hidden="true">
          <div className="strip-inner">
            {portfolioImages
              // three passes to create the seamless loop
              .concat(portfolioImages)
              .concat(portfolioImages)
              .map((src, idx) => (
                <img key={idx} src={src} alt="" loading="lazy" decoding="async" />
              ))}
          </div>
        </div>
      </section>

      {/* ============================================
          WHY WEBSITE MATTERS SECTION
      ============================================ */}
      <section className="py-24 md:py-32 relative" data-testid="why-website-matters-section">
        <div className="section-container">
          {/* Row 1: Text Left, Image Right */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center mb-24"
          >
            {/* Text Content */}
            <motion.div variants={fadeInLeft} className="order-2 lg:order-1">
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black mb-8 leading-[1.05] tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="why-website-headline-1"
              >
                Do you have a great business but barely anyone{' '}
                <span className="text-cyan-400">finds you online?</span>
              </h2>
              <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed max-w-lg">
                Many local businesses offer excellent services, but their website is outdated, unclear, or invisible on Google. If customers cannot quickly understand what you do, trust your brand, and contact you, you lose opportunities every day.
              </p>
              <p className="text-base sm:text-lg text-gray-400 leading-relaxed">
                We help businesses build modern, high-converting websites that look professional, explain the offer clearly, and turn visitors into real enquiries.
              </p>
            </motion.div>

            {/* Image */}
            <motion.div variants={fadeInRight} className="order-1 lg:order-2">
              <div className="relative group">
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80"
                  alt="Business website analysis"
                  className="w-full h-80 lg:h-[28rem] object-cover rounded-3xl shadow-2xl transition-transform duration-700 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              </div>
            </motion.div>
          </motion.div>

          {/* Row 2: Image Left, Text Right */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center"
          >
            {/* Image */}
            <motion.div variants={fadeInLeft} className="order-1">
              <div className="relative group">
                <img
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80"
                  alt="Modern website design"
                  className="w-full h-80 lg:h-[28rem] object-cover rounded-3xl shadow-2xl transition-transform duration-700 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              </div>
            </motion.div>

            {/* Text Content */}
            <motion.div variants={fadeInRight} className="order-2">
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black mb-8 leading-[1.05] tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="why-website-headline-2"
              >
                We build websites that help your business{' '}
                <span className="text-cyan-400">get seen and win more customers</span>
              </h2>
              <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed max-w-lg">
                Our goal is not just to make your website look good. We create clean, modern websites that build trust, improve your online presence, and make it easier for people to choose your business over competitors.
              </p>
              <p className="text-base sm:text-lg text-gray-400 leading-relaxed">
                From structure and design to clarity and conversion, every section is built to help your business grow online.
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Section divider */}
        <div className="absolute bottom-0 left-0 right-0 section-divider" />
      </section>

      {/* ============================================
          CHOOSE YOUR NEXT STEP SECTION
      ============================================ */}
      <section className="py-24 md:py-32 relative" data-testid="next-step-section">
        <div className="section-container">
          {/* Section Header */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-16 md:mb-20"
          >
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black mb-6 leading-[1.05] tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="next-step-heading"
            >
              Choose how you want to move forward
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Whether you are ready to start now or still exploring ideas, we make the next step simple.
            </p>
          </motion.div>

          {/* Two Large Cards Grid */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16"
          >
            {/* Card 1: Process */}
            <motion.div 
              variants={fadeInUp}
              className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 border border-gray-700 hover:border-cyan-500/50 transition-all duration-500"
              data-testid="next-step-card-1"
            >
              {/* Background Image */}
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80"
                  alt="Website design process"
                  className="w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
              </div>

              {/* Content */}
              <div className="relative z-10 p-8 lg:p-10 h-full flex flex-col">
                {/* Image section */}
                <div className="mb-8 h-48 rounded-2xl overflow-hidden bg-slate-800 border border-gray-700">
                  <img
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=600&q=80"
                    alt="Design process"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>

                {/* Text content */}
                <h3
                  className="text-2xl sm:text-3xl font-black mb-4 leading-tight tracking-tight text-white"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  A simple process from idea to launch
                </h3>
                <p className="text-base sm:text-lg text-gray-300 mb-8 leading-relaxed flex-grow">
                  We keep everything clear and efficient — from planning and design to the final build. You do not need to figure it all out alone. We guide the project and turn your idea into a modern website that is ready to represent your business properly.
                </p>

                {/* Button */}
                <Link
                  to="/contact"
                  className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  Start Your Project
                  <ArrowRight size={18} />
                </Link>
              </div>
            </motion.div>

            {/* Card 2: Services & Features */}
            <motion.div 
              variants={fadeInUp}
              className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 border border-gray-700 hover:border-cyan-500/50 transition-all duration-500"
              data-testid="next-step-card-2"
            >
              {/* Background Image */}
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80"
                  alt="Custom website features"
                  className="w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
              </div>

              {/* Content */}
              <div className="relative z-10 p-8 lg:p-10 h-full flex flex-col">
                {/* Image section */}
                <div className="mb-8 h-48 rounded-2xl overflow-hidden bg-slate-800 border border-gray-700">
                  <img
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=600&q=80"
                    alt="Website features and services"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>

                {/* Text content */}
                <h3
                  className="text-2xl sm:text-3xl font-black mb-4 leading-tight tracking-tight text-white"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Built for your business type
                </h3>
                <p className="text-base sm:text-lg text-gray-300 mb-8 leading-relaxed flex-grow">
                  We build custom websites for local services, fitness studios, beauty salons, restaurants, real estate, personal brands, and more. Every website includes responsive design, clear structure, modern UI, trust-building sections, and conversion-focused layouts.
                </p>

                {/* Button */}
                <Link
                  to="/contact"
                  className="btn-secondary inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Section divider */}
        <div className="absolute bottom-0 left-0 right-0 section-divider" />
      </section>

      {/* ============================================
          TRUST & TESTIMONIALS SECTION
      ============================================ */}
      <section className="py-32 md:py-40 relative" data-testid="trust-section">
        {/* Subtle background gradient */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] rounded-full opacity-20"
            style={{
              background: 'radial-gradient(ellipse, rgba(6, 182, 212, 0.03) 0%, transparent 70%)',
            }}
          />
        </div>

        <div className="section-container relative z-10">
          {/* Section Heading */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-24"
          >
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black mb-6 leading-[1.05] tracking-tight text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="trust-heading"
            >
              Trusted by businesses that want a stronger online presence
            </h2>
          </motion.div>

          {/* Trust Blocks Grid */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-16 lg:gap-20 mb-32"
          >
            {/* Block 1: Professional */}
            <motion.div
              variants={fadeInUp}
              className="text-center group"
              data-testid="trust-block-professional"
            >
              <div className="mb-6">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/20 flex items-center justify-center group-hover:border-cyan-400/40 transition-all duration-500">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-cyan-500"></div>
                </div>
              </div>
              <h3
                className="text-2xl sm:text-3xl font-black mb-4 text-white leading-tight"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Professional
              </h3>
              <p className="text-base sm:text-lg text-gray-300 leading-relaxed max-w-xs mx-auto">
                Websites designed to build trust from the first visit.
              </p>
            </motion.div>

            {/* Block 2: Strategic */}
            <motion.div
              variants={fadeInUp}
              className="text-center group"
              data-testid="trust-block-strategic"
            >
              <div className="mb-6">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/20 flex items-center justify-center group-hover:border-cyan-400/40 transition-all duration-500">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-cyan-500"></div>
                </div>
              </div>
              <h3
                className="text-2xl sm:text-3xl font-black mb-4 text-white leading-tight"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Strategic
              </h3>
              <p className="text-base sm:text-lg text-gray-300 leading-relaxed max-w-xs mx-auto">
                Clear structure and messaging built around your business goals.
              </p>
            </motion.div>

            {/* Block 3: Tailored */}
            <motion.div
              variants={fadeInUp}
              className="text-center group"
              data-testid="trust-block-tailored"
            >
              <div className="mb-6">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/20 flex items-center justify-center group-hover:border-cyan-400/40 transition-all duration-500">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-cyan-500"></div>
                </div>
              </div>
              <h3
                className="text-2xl sm:text-3xl font-black mb-4 text-white leading-tight"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Tailored
              </h3>
              <p className="text-base sm:text-lg text-gray-300 leading-relaxed max-w-xs mx-auto">
                Every project is shaped to fit your service, audience, and brand.
              </p>
            </motion.div>
          </motion.div>

          {/* Testimonials Grid */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10"
          >
            {/* Testimonial 1 */}
            <motion.div
              variants={fadeInUp}
              className="group relative bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border border-gray-700/50 rounded-3xl p-8 lg:p-10 hover:border-cyan-500/30 hover:shadow-[0_0_60px_-20px_rgba(6,182,212,0.15)] transition-all duration-500"
              data-testid="testimonial-1"
            >
              <div className="absolute top-6 left-6 w-2 h-2 rounded-full bg-cyan-400/60"></div>
              <p className="text-gray-300 text-base sm:text-lg mb-8 leading-relaxed italic pl-4 border-l border-gray-700/50">
                "Working with Bsquare made the whole process simple. The new website looks far more professional and gives our business a much stronger online presence."
              </p>
              <div className="border-t border-gray-700/50 pt-6">
                <p className="font-black text-white text-sm mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Michael D.
                </p>
                <p className="text-gray-400 text-xs uppercase tracking-wider font-medium">
                  Local Service Business, Dublin
                </p>
              </div>
            </motion.div>

            {/* Testimonial 2 */}
            <motion.div
              variants={fadeInUp}
              className="group relative bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border border-gray-700/50 rounded-3xl p-8 lg:p-10 hover:border-cyan-500/30 hover:shadow-[0_0_60px_-20px_rgba(6,182,212,0.15)] transition-all duration-500"
              data-testid="testimonial-2"
            >
              <div className="absolute top-6 left-6 w-2 h-2 rounded-full bg-cyan-400/60"></div>
              <p className="text-gray-300 text-base sm:text-lg mb-8 leading-relaxed italic pl-4 border-l border-gray-700/50">
                "The design feels modern, clear, and premium. It finally looks like a business website we are proud to send customers to."
              </p>
              <div className="border-t border-gray-700/50 pt-6">
                <p className="font-black text-white text-sm mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Sarah K.
                </p>
                <p className="text-gray-400 text-xs uppercase tracking-wider font-medium">
                  Beauty & Wellness Brand, Ireland
                </p>
              </div>
            </motion.div>

            {/* Testimonial 3 */}
            <motion.div
              variants={fadeInUp}
              className="group relative bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border border-gray-700/50 rounded-3xl p-8 lg:p-10 hover:border-cyan-500/30 hover:shadow-[0_0_60px_-20px_rgba(6,182,212,0.15)] transition-all duration-500"
              data-testid="testimonial-3"
            >
              <div className="absolute top-6 left-6 w-2 h-2 rounded-full bg-cyan-400/60"></div>
              <p className="text-gray-300 text-base sm:text-lg mb-8 leading-relaxed italic pl-4 border-l border-gray-700/50">
                "What stood out most was the clarity of the process. Everything felt organised, and the final result matched exactly the quality we wanted."
              </p>
              <div className="border-t border-gray-700/50 pt-6">
                <p className="font-black text-white text-sm mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  David R.
                </p>
                <p className="text-gray-400 text-xs uppercase tracking-wider font-medium">
                  Home Improvement Business, Ireland
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Section divider */}
        <div className="absolute bottom-0 left-0 right-0 section-divider" />
      </section>

      {/* ============================================
          FINAL CTA SECTION
      ============================================ */}
      <section className="py-24 md:py-32 bg-[#0d0d0d] relative" data-testid="final-cta-section">
        {/* Glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full opacity-40 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse, rgba(6, 182, 212, 0.08) 0%, transparent 70%)',
          }}
        />

        <div className="section-container relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="final-cta-headline"
            >
              Ready to Build Your Website?
            </motion.h2>

            <motion.p
              variants={fadeInUp}
              className="text-gray-400 text-lg mb-10 max-w-xl mx-auto"
            >
              Let's create something amazing together. Get in touch today.
            </motion.p>

            <motion.div variants={fadeInUp}>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-3 px-10 py-5 text-lg"
                data-testid="final-cta-button"
              >
                Start Your Project
                <ArrowRight size={20} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
